import { Button, useMediaQuery } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router';
import { LeftPanelSectionTemplate } from '../../../components/AdminTemplates';
import ConfirmationDialog, {
  ConsequencesList,
} from '../../../components/ConfirmationDialog';
import OutletContextReliantRoute from '../../../components/OutletContextReliantRoute';
import { SideActionItem } from '../../../components/SideActionsList';
import { useCurrentUser } from '../../../contexts/currentUser';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import { disableNavigationGuard } from '../../../hooks/useNavigationGuard';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import { Contract } from '../../../types/contract';
import { Route } from '../../../types/route';
import { StatusIcon } from '../../../utils/icon';
import { ContractTemplateContext } from './types';
import { RouteStatus } from '../../../types/core';
import ContractBadge from './ContractBadge';
import { useSnackbar } from 'notistack';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';
import { getContractBadgeStatus } from './utils';
import { MINIMUM_AMOUNT_OF_VESSELS_PER_OPERATOR } from '../../../types/vessel';
import { Role } from '../../../types/user';

const ContractTemplate = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { hasRole } = useCurrentUser();
  const isCurrentUserAdmin = hasRole(Role.ADMIN);
  const { contractId: contractIdQueryParam } = useParams();
  const contractId = useMemo(() => {
    return typeof contractIdQueryParam !== 'string' ||
      contractIdQueryParam.toLocaleLowerCase() === 'create'
      ? null
      : contractIdQueryParam;
  }, [contractIdQueryParam]);
  const {
    getContractById,
    getContractInsurances,
    getContractRoutes,
    getContractAssignedVessels,
    deleteContract,
    rescindContract,
  } = useIotsApi();
  const isMdUp = useMediaQuery('(min-width:768px)');

  const [displayedContractName, setDisplayedContractName] =
    useState<string>('');

  const [contractToDisable, setContractToDisable] = useState<Contract | null>(
    null,
  );

  const {
    data: contract = null,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['contract', contractId ?? ''],
    queryFn: () => getContractById(contractId ?? ''),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: contractId !== null,
  });

  const {
    data: fetchedRoutes = [],
    isLoading: isLoadingRoutes,
    isFetching: isFetchingRoutes,
    error: routesError,
    refetch: refetchRoutes,
  } = useQuery({
    queryKey: ['contract', contract?.id ?? '', 'routes'],
    queryFn: () => getContractRoutes(contract?.id ?? ''),
    enabled: contract !== null,
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    select: routes =>
      [...routes].sort((a, b) => {
        if (a.routeStatus !== b.routeStatus) {
          return a.routeStatus === RouteStatus.Disabled ? 1 : -1;
        }
        return (
          a.dateFrom.localeCompare(b.dateFrom) ||
          a.dateTo.localeCompare(b.dateTo)
        );
      }),
  });

  const {
    data: assignedVessels = null,
    isLoading: isLoadingAssignedVessels,
    isFetching: isFetchingAssignedVessels,
    isPending: areAssignedVesselsPending,
    error: assignedVesselsError,
    refetch: refetchAssignedVessels,
  } = useQuery({
    queryKey: ['contract', contract?.id ?? '', 'vessels'],
    queryFn: () => getContractAssignedVessels(contract?.id ?? ''),
    enabled: contract !== null,
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const assignedVesselsCount = !assignedVessels
    ? 0
    : 1 + assignedVessels.backups.length;

  const {
    data: contractInsurances = [],
    isFetching: isFetchingContractInsurances,
    error: contractInsurancesError,
    refetch: refecthInsurances,
  } = useQuery({
    queryKey: ['contract', contract?.id ?? '', 'insurances'],
    queryFn: () => getContractInsurances(contract?.id ?? ''),
    enabled: contract !== null,
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    select: insurances =>
      [...insurances].sort(
        (a, b) =>
          (a.isExpired ? 0 : 1) - (a.isExpired ? 0 : 1) ||
          b.endDate.localeCompare(a.endDate),
      ),
  });

  useQueryErrorNotifier([
    error,
    routesError,
    assignedVesselsError,
    contractInsurancesError,
  ]);

  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();

  const disableContractMutation = useMutation({
    mutationFn: (): Promise<void> => {
      if (!contractToDisable) {
        return Promise.reject(new Error());
      }
      if (contractToDisable.isDeletable) {
        return deleteContract(contractToDisable.id).then(() => {});
      }
      return rescindContract(contractToDisable.id).then(() => {});
    },
    onSuccess: () => {
      if (!contractToDisable) {
        return;
      }
      enqueueSnackbar({
        variant: 'success',
        message: t(
          `content.contract-${contractToDisable.isDeletable ? 'deletion' : 'rescision'}-success`,
        ),
      });
      if (contractToDisable.isDeletable) {
        void disableNavigationGuard(() => navigate('/admin/contracts'));
      }
      void refetch();
      void refetchRoutes();
      void refetchAssignedVessels();
      void refecthInsurances();
      setContractToDisable(null);
    },
    onError: error => {
      if (!contractToDisable) {
        return;
      }
      enqueueSnackbar({
        variant: 'error',
        message: getErrorMessage(
          error,
          t(
            `content.contract-${contractToDisable.isDeletable ? 'deletion' : 'rescision'}-error`,
          ),
        ),
      });
      disableContractMutation.reset();
    },
  });

  const isContractExpired = Boolean(
    contract && dayjs(contract.dateTo)?.isBefore(dayjs(), 'day'),
  );

  const queryClient = useQueryClient();

  const sidePanelActions = useMemo<SideActionItem[]>(
    () => [
      {
        id: 'basic',
        label: t('content.basic-information'),
        statusIcon: contract ? StatusIcon.Completed : StatusIcon.Pending,
        url: `/admin/contracts/${contractId ?? 'create'}`,
        isExactUrl: true,
      },
      {
        id: 'timetable',
        label: t('content.timetable'),
        statusIcon:
          fetchedRoutes.length > 0 ? StatusIcon.Completed : StatusIcon.Pending,
        disabled: !contract && t('validation.timetable-disabled-reason'),
        url: contractId
          ? `/admin/contracts/${contractId}/timetable`
          : undefined,
      },
      {
        id: 'insurance',
        label: t('content.insurance'),
        statusIcon:
          !contract || isFetchingContractInsurances
            ? StatusIcon.Pending
            : contract.isValidInsurance
              ? StatusIcon.Completed
              : contractInsurances.length === 0
                ? StatusIcon.Error
                : StatusIcon.Warning,
        disabled:
          !contract && t('validation.contract-insurance-disabled-reason'),
        url: contractId
          ? `/admin/contracts/${contractId}/insurance`
          : undefined,
      },
      {
        id: 'vessels',
        label: t('content.vessels'),
        statusIcon: areAssignedVesselsPending
          ? StatusIcon.Pending
          : assignedVesselsCount === 0
            ? StatusIcon.Error
            : assignedVesselsCount < MINIMUM_AMOUNT_OF_VESSELS_PER_OPERATOR
              ? StatusIcon.Warning
              : StatusIcon.Completed,
        disabled: !contract && t('validation.contract-vessels-disabled-reason'),
        url: contractId ? `/admin/contracts/${contractId}/vessels` : undefined,
      },
    ],
    [
      t,
      contract,
      contractId,
      fetchedRoutes.length,
      isFetchingContractInsurances,
      contractInsurances.length,
      areAssignedVesselsPending,
      assignedVesselsCount,
    ],
  );

  const contextValue = useMemo<ContractTemplateContext>(
    () => ({
      contractId,
      contract,
      isContractDisabled: isContractExpired,
      updateContextContract: updatedContract =>
        queryClient.setQueryData<Contract>(
          ['contract', contractId ?? ''],
          updatedContract,
        ),
      setDisplayedContractName,
      isFetching,
      error,
      refetchContract: () => void refetch(),
      routesManagement: {
        routes: fetchedRoutes,
        isFetching: isFetchingRoutes,
        isLoading: isLoadingRoutes,
        error: routesError,
        refetch: () => void refetchRoutes(),
        addRouteToContext: route =>
          queryClient.setQueryData<Route[]>(
            ['contract', contract?.id ?? '', 'routes'],
            prev => [route, ...(prev ?? [])],
          ),
        removeRouteFromContext: routeId =>
          queryClient.setQueryData<Route[]>(
            ['contract', contract?.id ?? '', 'routes'],
            prev => prev?.filter(({ id }) => id !== routeId) ?? [],
          ),
      },
      insuranceManagement: {
        insurances: contractInsurances,
        isFetching: isFetchingContractInsurances,
        refetch: () => void refecthInsurances(),
      },
      assignedVesselsManagement: {
        assignedVessels,
        isLoading: isLoadingAssignedVessels,
        isFetching: isFetchingAssignedVessels,
        error: assignedVesselsError,
        refetch: () => void refetchAssignedVessels(),
      },
    }),
    [
      assignedVessels,
      assignedVesselsError,
      contract,
      contractId,
      contractInsurances,
      error,
      fetchedRoutes,
      isContractExpired,
      isFetching,
      isFetchingAssignedVessels,
      isFetchingContractInsurances,
      isFetchingRoutes,
      isLoadingAssignedVessels,
      isLoadingRoutes,
      queryClient,
      refetch,
      refecthInsurances,
      refetchAssignedVessels,
      refetchRoutes,
      routesError,
    ],
  );

  return (
    <LeftPanelSectionTemplate
      title={
        <span className="inline-flex flex-wrap items-center gap-2 md:items-end md:gap-6">
          {contract?.name ??
            (displayedContractName.trim() || t('content.untitled'))}
          {contract && !areAssignedVesselsPending && (
            <ContractBadge
              status={getContractBadgeStatus(
                isContractExpired,
                assignedVesselsCount,
                contract.isValidInsurance,
              )}
              small={!isMdUp}
            />
          )}
        </span>
      }
      actions={
        isCurrentUserAdmin && contract && !isContractExpired ? (
          <>
            <Button
              type="button"
              variant="contained"
              color="error"
              onClick={() => {
                setContractToDisable(contract);
              }}
            >
              {t(`content.${contract.isDeletable ? 'delete' : 'rescind'}`)}
            </Button>
            <ConfirmationDialog
              open={Boolean(contractToDisable)}
              title={t(
                `content.contract-${contract.isDeletable ? 'deletion' : 'rescision'}-confirmation.title`,
              )}
              loading={disableContractMutation.isPending}
              onClose={() => setContractToDisable(null)}
              onConfirm={() => {
                disableContractMutation.mutate();
              }}
            >
              <ConsequencesList
                action={t(
                  `content.contract-${contract.isDeletable ? 'deletion' : 'rescision'}-confirmation.action`,
                )}
                list={
                  contract.isDeletable
                    ? [
                        t(
                          `content.contract-deletion-confirmation.bullet-points.will-be-visually-removed`,
                        ),
                        t(
                          `content.contract-deletion-confirmation.bullet-points.no-related-journeys-exist`,
                        ),
                      ]
                    : [
                        t(
                          'content.contract-rescision-confirmation.bullet-points.related-elements-end-date-will-be-set-to-today',
                        ),
                        t(
                          'content.contract-rescision-confirmation.bullet-points.journeys-before-today-still-interactive',
                        ),
                      ]
                }
              />
            </ConfirmationDialog>
          </>
        ) : null
      }
      isLoading={
        isFetching ||
        isFetchingRoutes ||
        isFetchingAssignedVessels ||
        isFetchingContractInsurances
      }
      leftPanelActions={sidePanelActions}
      goBackLinkText={t('content.back-to-contracts')}
      goBackLinkUrl="/admin/contracts"
    >
      <Outlet context={contextValue} />
    </LeftPanelSectionTemplate>
  );
};

export default ContractTemplate;

export const ContractSubPageWrapper = () => (
  <OutletContextReliantRoute<ContractTemplateContext>
    getOutletContextState={({ contract, contractId, isFetching }) =>
      contract ? 'ready' : contractId && isFetching ? 'pending' : 'error'
    }
    errorRedirectionPath="/admin/contracts"
  />
);
